// Extension included so `npm test` can run this file directly under Node's ESM
// resolver, which (unlike Vite) does not guess at missing extensions.
import { buildFormationSlots, ZONES } from './formation.js';
import { RATING_NEUTRAL } from './constants.js';

/**
 * The overall rating, averaged over the seven attributes every player has.
 *
 * Goalkeeping is deliberately left out. It is only stored for players who list
 * GK, so including it would mean keepers were averaged over eight numbers and
 * everyone else over seven — two different scales silently compared as one. It
 * would also distort the common case for a casual game: most weeks nobody plays
 * in goal at all, so a specialist keeper who is ordinary outfield would be rated
 * as better than they are in the games they actually play. Goalkeeping is used
 * where it belongs instead — deciding who goes in goal.
 */
export function computeBaseOverall(attributes) {
  const vals = Object.entries(attributes || {})
    .filter(([key, v]) => typeof v === 'number' && key !== 'goalkeeping')
    .map(([, v]) => v);
  if (vals.length === 0) return 5;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/**
 * A player's rating, stacked from three sources:
 *   attributes    a human's deliberate assessment (never auto-edited)
 *   rating_offset what results have taught us since (persistent)
 *   form          a recent hot/cold streak (decays weekly)
 */
export function effectiveRating(player) {
  const base = computeBaseOverall(player?.attributes);
  const offset = Number(player?.rating_offset) || 0;
  const form = Number(player?.form) || 0;
  return Math.max(1, Math.min(10, base + offset + form));
}

export function primaryPosition(player) {
  return (player?.positions && player.positions[0]) || 'FLEX';
}

// How much better a player must be to take a slot ahead of someone who ranks
// that position higher on their own list. Strict preference order (the old
// behaviour) made every pick deterministic, so Reshuffle returned the same
// teams over and over; blending preference into the score instead keeps
// position fit honest while letting near-equal players trade places.
const PREF_WEIGHT = 1.2;

// How far below the best available candidate a player can be and still be in
// the running for a slot. Roughly "one point of ability", which is inside the
// noise of anyone's honest guess at a teammate's rating.
const CHOICE_WINDOW = 1.0;

// How strongly recent stints in goal push someone toward an outfield spot.
// Nobody signs up for football to spend every week in goal, and rating-only
// balancing is completely blind to that — it is not a fairness the numbers can
// see, but it is the one people actually complain about.
const KEEPER_FATIGUE = 0.9;

// How much the Goalkeeping attribute counts when filling the GK slot. A player
// rated 9 in goal gets +1.4 towards it, one rated 2 gets -1.05 — enough to sort
// the specialists from the volunteers without overriding a strong preference.
const GK_WEIGHT = 0.35;

// How strongly the balancer avoids putting the same two people together week
// after week. Small on purpose: a fair split matters more than a varied one, so
// this only breaks ties between splits that are already close on rating.
const PAIR_WEIGHT = 0.06;

export const pairKey = (a, b) => (a < b ? `${a}|${b}` : `${b}|${a}`);

/**
 * Ranks everyone still unpicked for a given position:
 *   1. players who list it — by rating, penalised by how far down their list it is
 *   2. players who list *something in the same zone* (defence/midfield/attack)
 *   3. whoever is left, by rating
 *
 * For goalkeeper, anyone who has kept goal recently is pushed down the list, so
 * the job rotates instead of landing on the same person every week.
 */
function rankCandidates(pool, position, history) {
  const keeperPenalty = (p) =>
    position === 'GK' ? (history?.gkCount?.[p.id] || 0) * KEEPER_FATIGUE : 0;

  // Goalkeeping counts for exactly one thing: this slot.
  const keeperSkill = (p) =>
    position === 'GK' ? ((p.attributes?.goalkeeping ?? 5) - 5) * GK_WEIGHT : 0;

  // `fit` is how well someone suits this slot right now: ability, minus how far
  // down their own preference list it is, minus how recently they were stuck in
  // goal. Everything downstream ranks and samples on fit — an earlier version
  // sorted by fit but then sampled on raw rating, which silently discarded both
  // penalties and made keeper rotation a no-op.
  const rank = (list, fit) =>
    list.map((p) => ({ player: p, fit: fit(p) })).sort((x, y) => y.fit - x.fit);

  const listed = pool.filter((p) => (p.positions || []).includes(position));
  if (listed.length) {
    return rank(
      listed,
      (p) => p._score - p.positions.indexOf(position) * PREF_WEIGHT - keeperPenalty(p) + keeperSkill(p)
    );
  }

  const zone = ZONES[position];
  const sameZone = pool.filter((p) => (p.positions || []).some((pp) => ZONES[pp] === zone));
  if (sameZone.length) return rank(sameZone, (p) => p._score - keeperPenalty(p) + keeperSkill(p));

  return rank(pool, (p) => p._score - keeperPenalty(p) + keeperSkill(p));
}

/**
 * How much of a rerun this team is. Sums how often each pair in it has already
 * been on the same side, so a split that reunites the usual clique scores badly.
 */
function familiarity(team, history) {
  const counts = history?.pairCount;
  if (!counts) return 0;
  let total = 0;
  for (let i = 0; i < team.length; i++) {
    for (let j = i + 1; j < team.length; j++) {
      total += counts[pairKey(team[i].id, team[j].id)] || 0;
    }
  }
  return total;
}

/**
 * Splits attendees into two teams, giving every player a specific pitch
 * position rather than a vague category.
 *
 * Both teams' formations are walked in lockstep, slot by slot. At each step the
 * two best candidates for that position are picked and handed out one per team,
 * stronger player to whichever side is currently weaker. That does three things
 * at once: each team gets its own goalkeeper, like-for-like players are compared
 * head to head, and neither team can end up with more players than the other.
 *
 * (The previous version picked all the players first and *then* dealt them out
 * by running rating total alone, which could put both goalkeepers on the same
 * team and produce a 7 v 4 split. Both are fixed here.)
 */
export function balanceTeamsWithPositions(players, history = null) {
  const n = players.length;
  const sizeA = Math.ceil(n / 2);
  const sizeB = n - sizeA;

  // Only shape the teams around goalkeepers if there are enough real ones to
  // give each side its own. With one or none, GK slots get filled by whoever is
  // left over — which labels someone a keeper who never said they were, and in
  // the one-keeper case leaves the sides asymmetric. Most kickabouts have no
  // designated keeper at all and rotate during the game; this lets them.
  const keepers = players.filter((p) => (p.positions || []).includes('GK')).length;
  const includeKeeper = keepers >= 2;

  const slotsA = buildFormationSlots(sizeA, { includeKeeper });
  const slotsB = buildFormationSlots(sizeB, { includeKeeper });

  let pool = players.map((p) => {
    const rating = effectiveRating(p);
    return {
      ...p,
      _rating: rating,
      // Random nudge so the same squad doesn't produce an identical split every
      // week. Wide enough that near-equal players genuinely trade places, not
      // so wide that a 4 outranks an 8.
      _score: rating + (Math.random() * 1.6 - 0.8),
    };
  });

  const teamA = [];
  const teamB = [];
  let sumA = 0;
  let sumB = 0;

  const place = (team, entry, position) => {
    const placed = { ...entry, assignedPosition: position, isManualOverride: false };
    if (team === 'A') {
      teamA.push(placed);
      sumA += placed._rating;
    } else {
      teamB.push(placed);
      sumB += placed._rating;
    }
    pool = pool.filter((p) => p !== entry);
  };

  // Rather than always taking the single best candidate — which made the whole
  // split deterministic and Reshuffle pointless — pick at random from everyone
  // within CHOICE_WINDOW of the best. They all list the position (or all fall
  // into the same fallback tier), so nobody ends up somewhere daft; it just
  // means two near-equal players genuinely trade places week to week.
  const take = (position) => {
    const ranked = rankCandidates(pool, position, history);
    if (!ranked.length) return null;
    const best = ranked[0].fit;
    const viable = ranked.slice(0, 4).filter((c) => best - c.fit <= CHOICE_WINDOW);
    return (viable[Math.floor(Math.random() * viable.length)] || ranked[0]).player;
  };

  const maxLen = Math.max(slotsA.length, slotsB.length);
  for (let i = 0; i < maxLen; i++) {
    const posA = slotsA[i];
    const posB = slotsB[i];

    if (posA && posB) {
      const first = take(posA);
      if (!first) break;
      // Provisionally remove so the second pick can't be the same person.
      pool = pool.filter((p) => p !== first);
      const second = take(posB);
      pool.push(first);

      if (!second) {
        place(sumA <= sumB ? 'A' : 'B', first, posA);
        continue;
      }
      // Stronger of the two goes to whichever side is currently behind.
      const [strong, weak] = first._rating >= second._rating ? [first, second] : [second, first];
      if (sumA <= sumB) {
        place('A', strong, posA);
        place('B', weak, posB);
      } else {
        place('B', strong, posB);
        place('A', weak, posA);
      }
    } else if (posA) {
      const only = take(posA);
      if (only) place('A', only, posA);
    } else if (posB) {
      const only = take(posB);
      if (only) place('B', only, posB);
    }
  }

  // Anything left over (shouldn't happen, but never drop a player silently).
  while (pool.length) {
    const next = pool[0];
    const target = teamA.length <= teamB.length ? 'A' : 'B';
    const preferred = primaryPosition(next);
    const fallback = preferred === 'FLEX' || (!includeKeeper && preferred === 'GK') ? 'CM' : preferred;
    place(target, next, fallback);
  }

  const improved = tightenGap(teamA, teamB, history);
  return {
    teamA: improved.teamA,
    teamB: improved.teamB,
    sumA: improved.teamA.reduce((s, p) => s + p._rating, 0),
    sumB: improved.teamB.reduce((s, p) => s + p._rating, 0),
    keeperless: !includeKeeper,
    keepersAvailable: keepers,
  };
}

/**
 * Cleanup pass: swap any pair of players in the same position on opposite teams
 * where doing so brings the two average ratings closer together. Only
 * same-position swaps are considered, so both formations stay intact.
 *
 * It deliberately stops as soon as the gap is inside GOOD_ENOUGH rather than
 * grinding to the mathematical optimum. Chasing the optimum makes the result
 * almost deterministic, which turns Reshuffle into a button that hands you back
 * the same eleven — a 0.1 rating difference is invisible on grass, and a genuine
 * choice of line-ups is worth more than a third decimal place.
 */
const GOOD_ENOUGH = 0.12;

function tightenGap(teamA, teamB, history) {
  const a = [...teamA];
  const b = [...teamB];

  const gap = () =>
    Math.abs(
      a.reduce((s, p) => s + p._rating, 0) / (a.length || 1) -
        b.reduce((s, p) => s + p._rating, 0) / (b.length || 1)
    );

  // Once the rating gap is inside GOOD_ENOUGH, keep swapping only while it
  // buys variety — so among splits that are equally fair, the one that breaks
  // up the usual pairings wins.
  const cost = () => gap() + PAIR_WEIGHT * (familiarity(a, history) + familiarity(b, history));

  let improvedSomething = true;
  let guard = 0;
  while (improvedSomething && guard++ < 20) {
    improvedSomething = false;
    for (let i = 0; i < a.length; i++) {
      for (let j = 0; j < b.length; j++) {
        if (a[i].assignedPosition !== b[j].assignedPosition) continue;

        // Fairness first: while the sides are still uneven, only rating counts.
        const chasingFairness = gap() > GOOD_ENOUGH;
        const score = chasingFairness ? gap : cost;
        const before = score();

        [a[i], b[j]] = [b[j], a[i]];

        // Never buy variety at the price of a split that is no longer fair.
        const acceptable = chasingFairness || gap() <= GOOD_ENOUGH;
        if (acceptable && score() < before - 0.0001) {
          improvedSomething = true;
          break;
        }
        [a[i], b[j]] = [b[j], a[i]];
      }
    }
  }
  return { teamA: a, teamB: b };
}

/** How much of last week's form carries into this week. */
export const FORM_DECAY = 0.6;

/**
 * How hard the soft cap bites. 0 turns it off entirely (every player gains and
 * loses at the same rate). 1.5 means a 10-rated player gains at a quarter speed
 * and loses at nearly double, with the two crossing at 1.0 for a mid player.
 * Raise it to make the ladder steeper; lower it to make ratings move more freely.
 */
export const RESISTANCE = 1.5;

/**
 * The higher a player already rates, the harder it is to climb further and the
 * easier it is to slip — and the reverse at the bottom.
 *
 * Without this, form is a straight accumulator: your best player has exactly as
 * easy a time going from 9 to 9.5 as your weakest has going from 4 to 4.5, so
 * good players ratchet upward and the balancer's job gets harder every week.
 * A rating is a claim about where someone sits relative to everyone else, and
 * the top of a range should cost more to hold than the middle.
 */
export function ratingResistance(rating) {
  const t = Math.max(0, Math.min(1, ((Number(rating) || 5) - 1) / 9));
  return {
    gain: Math.max(0.05, 1 + RESISTANCE * (0.5 - t)),
    loss: Math.max(0.05, 1 + RESISTANCE * (t - 0.5)),
  };
}

/**
 * Recomputes a player's form after a game. Form is a small, decaying modifier
 * layered on top of their manually-entered attributes — the attributes
 * themselves are never auto-edited, because those represent a deliberate human
 * assessment rather than one afternoon's output.
 *
 * `baseRating` is the average of those attributes. It's needed because the soft
 * cap keys off where the player actually sits, not just how hot they've been.
 */
export function nextForm({ currentForm, baseRating = 5, performance, goals, assists, outcome }) {
  const form = Number(currentForm) || 0;

  // Ratings are out of ten now. Dividing by 9 keeps this term in the same
  // ±0.5 band it occupied on the old five-point scale, so nobody's form
  // jumps the week the scale changed — only the resolution improves.
  const performanceDelta = ((performance ?? RATING_NEUTRAL) - RATING_NEUTRAL) / 9;
  const goalsDelta = Math.min((goals || 0) * 0.1, 0.3);
  const assistsDelta = Math.min((assists || 0) * 0.08, 0.24);
  const outcomeDelta = (outcome || 0) * 0.1;

  const rating = Math.max(1, Math.min(10, (Number(baseRating) || 5) + form));
  const { gain, loss } = ratingResistance(rating);

  // Split rather than scaling the net, so a strong individual game in a losing
  // side is still judged on both counts. Goals and assists are always earned,
  // so they only ever land on the gain side.
  const earned = Math.max(0, performanceDelta) + goalsDelta + assistsDelta + Math.max(0, outcomeDelta);
  const conceded = Math.min(0, performanceDelta) + Math.min(0, outcomeDelta);

  const raw = form * FORM_DECAY + earned * gain + conceded * loss;
  return Number(Math.max(-1.5, Math.min(1.5, raw)).toFixed(2));
}
